import React from 'react';
import { getDataTable } from '../../charts/chartDataBindings';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { TpDataContext } from '../../../../data/interfaces';
import styles from '../SingleItemView.module.scss';
import PagedTable, { TpPagedTableColumn, TpPagedTableData } from '../../../../components/pagedTable/PagedTable';
import { getCanonicalTypeInfo, getColumnInfo, performUserSelectionAction } from '../../../../data/analyticsDataSlice';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { useAppDispatch } from '../../../../app/hooks';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { SectionIndex, TpSectionsIndexContext } from '../../../../components/sections-index/SectionsIndex';
import { TpSingleEntityViewContext } from '../interfaces';
import { getUserError } from '../../../../components/miscUtils';

interface TpNetworkLinksProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate?: SingleEntityTemplate;
  elementTemplate: any;
  children?: React.ReactNode;
  sectionIndexContext: TpSectionsIndexContext;
  singleEntityViewContext: TpSingleEntityViewContext;
}

interface TpSourceColInfo {
  id: string;
  width?: number;
}

interface TpElementTemplate {
  title: string;
  nodesTableId: string;
  edgesTableId: string;
  columnsNodes: TpSourceColInfo[];
  columnsEdges: TpSourceColInfo[];
}

const NetworkLinks = (props: TpNetworkLinksProps) => {
  const dispatch = useAppDispatch();

  const { dataContext, itemId } = props;
  const elementTemplate = props.elementTemplate as TpElementTemplate;

  const nodesTable = getDataTable(dataContext, elementTemplate.nodesTableId);
  const edgesTable = getDataTable(dataContext, elementTemplate.edgesTableId);
  const nodedCanonicalTypeInfo = getCanonicalTypeInfo(dataContext, nodesTable.canonicalTypeId);

  if (nodesTable.canonicalTypeId != edgesTable.relationsForEntity) {
    throw getUserError(
      `Incompatible network: expected entity=${nodesTable.canonicalTypeId}, found=${edgesTable.relationsForEntity}`,
    );
  }

  // Build fast lookup to translate from node IDs to indexes
  const nodesItemIds = nodesTable.columnData[nodesTable.colItemId];
  const nodesMap: { [id: string]: number } = {};
  for (let i = 0; i < nodesItemIds.length; i++) {
    nodesMap[nodesItemIds[i]] = i;
  }

  const pagedTableData: TpPagedTableData = {
    rowCount: 0,
    columns: [],
  };

  // Assemble the columns that come from the nodes
  const nodeCols: TpPagedTableColumn[] = [];
  for (const colDef of elementTemplate.columnsNodes) {
    const colInfo = getColumnInfo(nodesTable, colDef.id);
    const tableCol: TpPagedTableColumn = {
      id: colDef.id,
      displayName: colInfo.name,
      data: [],
      width: colDef.width || 150,
      convertData2String: (val) => convertColumnDataToDisplayString(colInfo, val),
    };
    pagedTableData.columns.push(tableCol);
    nodeCols.push(tableCol);
  }

  // Assemble the columns that come from the edges
  const edgeCols: TpPagedTableColumn[] = [];
  for (const colDef of elementTemplate.columnsEdges) {
    const colInfo = getColumnInfo(edgesTable, colDef.id);
    const tableCol: TpPagedTableColumn = {
      id: colDef.id,
      displayName: colInfo.name,
      data: [],
      width: 150,
      convertData2String: (val) => convertColumnDataToDisplayString(colInfo, val),
    };
    pagedTableData.columns.push(tableCol);
    edgeCols.push(tableCol);
  }

  const dataEdgesFrom = edgesTable.columnData[edgesTable.relationFrom!];
  const dataEdgesTo = edgesTable.columnData[edgesTable.relationTo!];

  // Iterate over all edges, find the ones that relate to the current entity, and add rows to the paged table data
  const relatedItemIds: string[] = [itemId];
  for (let edgeNr = 0; edgeNr < dataEdgesFrom.length; edgeNr++) {
    let relatedItemId = null;
    let isReverse = null;
    if (dataEdgesFrom[edgeNr] == itemId) {
      relatedItemId = dataEdgesTo[edgeNr];
      isReverse = false;
    }
    if (dataEdgesTo[edgeNr] == itemId) {
      relatedItemId = dataEdgesFrom[edgeNr];
      isReverse = true;
    }
    if (relatedItemId && relatedItemId in nodesMap) {
      if (!nodedCanonicalTypeInfo.restrictedItemsMap || relatedItemId in nodedCanonicalTypeInfo.restrictedItemsMap) {
        relatedItemIds.push(relatedItemId);
        pagedTableData.rowCount++;
        const relatedItemIdx = nodesMap[relatedItemId];
        for (const col of nodeCols) {
          col.data.push(nodesTable.columnData[col.id][relatedItemIdx]);
        }
        for (const col of edgeCols) {
          col.data.push(edgesTable.columnData[col.id][edgeNr]);
        }
      }
    }
  }

  function selectAllRelated() {
    dispatch(
      performUserSelectionAction({
        dataContextId: dataContext.id,
        canonicalTypeId: nodesTable.canonicalTypeId,
        userSelectionAction: {
          type: TpEnSelectionFilterTypes.MANUAL,
          description: `Network linked to ${props.itemId}`,
          method: 'replace',
          selectedItemIds: relatedItemIds,
        },
      }),
    );
  }

  function onRowClicked(rowNr: number, rowData: any) {
    const relatedItemId = rowData[nodesTable.colItemId];
    props.singleEntityViewContext.openEntity(nodedCanonicalTypeInfo.id, relatedItemId);
  }

  return (
    <SectionIndex
      sectionIndexContext={props.sectionIndexContext}
      sectionId={`networklinks_${elementTemplate.edgesTableId}_${elementTemplate.nodesTableId}`}
      sectionName={elementTemplate.title}
    >
      <div className={styles.tableTitle}>{`${elementTemplate.title} (${pagedTableData.rowCount})`}</div>
      {pagedTableData.rowCount == 0 && <div>No data found</div>}
      {pagedTableData.rowCount > 0 && (
        <>
          <FancyButton iconName="fa:circle-check" onClick={selectAllRelated}>
            Select all related
          </FancyButton>
          <div style={{ height: '20px' }} />
          <PagedTable data={pagedTableData} defaultSortColId={nodesTable.colItemId} onRowClicked={onRowClicked} />
        </>
      )}
    </SectionIndex>
  );
};

export default NetworkLinks;
